import { StatusIndicatorProps } from "@amzn/awsui-components-react/polaris/status-indicator";
import { AccountingEvent, AccountingEvents } from "../types/events";
import { PAYMENT_METHODS } from "../constants";
import { mapEmpProviderToPaymentMethod } from "./PaymentUtils";

export const isTvodAccountingEvent = (event: AccountingEvent) => {
  return Number(event.glProductGroup) === 318;
};

// Classic-flow settlement is a distinct SETTLEMENT-type accounting event
// (keyed <auid>#SETTLEMENT) carrying settlementClientId / paymentMethodChargeIds.
export const hasSettlementDetails = (accountingEvents: AccountingEvents) =>
  Object.values(accountingEvents || {}).some(
    (event) => event.type === "SETTLEMENT"
  );

const createState = (status: StatusIndicatorProps.Type, text: string) => ({
  status,
  text,
});

export const getPaymentMethod = (event: AccountingEvent): string => {
  if (event.isEmpOrder) {
    if (event.empProviderId) {
      return mapEmpProviderToPaymentMethod(event.empProviderId);
    }
    return PAYMENT_METHODS.EMP;
  }

  const paymentMethods = (event.paymentInstruments ?? []).map((payment) => {
    switch (payment.instrument) {
      case "BankAccountPaymentInstrument":
        return PAYMENT_METHODS.DIRECT_DEBIT;
      case "CreditCardPaymentInstrument":
        return PAYMENT_METHODS.CREDIT_CARD;
      case "GiftCardPaymentInstrument":
      case "GiftCertificatePaymentInstrument":
        return PAYMENT_METHODS.GIFT_CARD;
      case "UnmappedPaymentInstrument":
        return PAYMENT_METHODS.UNMAPPED_PAYMENT_INSTRUMENT;
      default:
        return payment.instrument;
    }
  });

  const uniquePaymentMethods = [...new Set(paymentMethods)];
  return uniquePaymentMethods.length
    ? uniquePaymentMethods.join(" + ")
    : PAYMENT_METHODS.AMP;
};

export const getPaymentState = (event: AccountingEvent) => {
  const method = getPaymentMethod(event);
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (!method) return createState("error", "No Payment Data");
  if (event.isFreeTrial)
    return createState("in-progress", `Free Trial (${method})`);
  if (events.includes("OrderSettleSucceeded"))
    return createState("success", `Paid (${method})`);
  if (events.includes("OrderCompleted-ClosedNonPayment"))
    return createState("error", `Failed (${method})`);
  return createState("in-progress", `Pending (${method})`);
};

export const getFulfillmentState = (event: AccountingEvent) => {
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (
    !isTvodAccountingEvent(event) &&
    !(events.includes("PeriodStart") || events.includes("Resubscribed"))
  ) {
    return createState("error", "No Period Start");
  }
  if (events.includes("Cancelled")) return createState("error", "Cancelled");
  if (events.includes("Suspended")) return createState("error", "Suspended");
  if (
    events.includes("PeriodStart") ||
    events.includes("Resubscribed") ||
    (isTvodAccountingEvent(event) && events.includes("OrderFulfilled"))
  ) {
    return createState("success", "Fulfilled");
  }

  return createState("in-progress", "Pending");
};

export const getRefundState = (event: AccountingEvent) => {
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (events.includes("RefundSucceeded"))
    return createState("success", "Refunded");
  if (events.includes("RefundFailed"))
    return createState("error", "Refund Failed");
  if (events.includes("ReturnCompleted-FreeReturn"))
    return createState("success", "Free Return");
  return createState("in-progress", "No Refund");
};
export const getEMPSubsOrchestrationPlatform = (event: AccountingEvent) => {
  const events = Object.keys(event.sourceEventsToVersion || {});

  if (events.includes("PeriodStart")) {
    return event.empPurchaseHandleId?.trim() ? "Thunderbird" : "Denarius";
  }
  return "Unknown (No PeriodStart)";
};
